import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { Routing } from '@core/constants/routing.const';
import { UserType } from '@core/enums/type.enum';
import { AuthService } from '@services/auth/auth.service';
import { CurrentUserService } from '@services/common/currentuser.service';
import { LocationFilterService } from '@services/common/location-filter.service';
import { PermissionService } from '@services/common/permission.service';

export const authGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const currentUserService = inject(CurrentUserService);
  const locationFilterService = inject(LocationFilterService);
  const permissionService = inject(PermissionService);

  await currentUserService.getAsync();

  const isAuthenticated = authService.isAuthenticated();
  const currentUser = currentUserService.get();

  if (isAuthenticated && currentUser?.userTypeId !== UserType.Patient) {
    const targetUrl = state?.url || router.url || '';

    // Allow root or empty path to redirect normally to child routes
    if (!targetUrl || targetUrl === '/' || targetUrl === `/${Routing.Secure}`) {
      return true;
    }

    const isAccessible = permissionService.checkAppModuleAccess(targetUrl);
    if (!isAccessible) {
      router.navigate([Routing.Forbidden]);
      return false;
    }

    if (!locationFilterService.isInitialized()) {
      if (currentUser) {
        const locations = (currentUser.userLocations ?? []).map(
          (ul: any) => ({ id: ul.locationIdEnc, name: ul.locationName })
        );
        locationFilterService.initialize(
          locations.length ? locations : (currentUser.locations ?? []),
          currentUser.userTypeId
        );
      }
    }

    return isAccessible;
  } else {
    const targetUrl = state?.url || router.url;
    router.navigate([Routing.Auth], {
      queryParams: { returnUrl: targetUrl || '/' }
    });
    currentUserService.IsUserLoggedIn.next(false);
    return false;
  }
};
